import {
  Download,
  Loader2,
  Maximize2,
  Pause,
  Play,
  UndoDot,
  RedoDot,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Client,settings } from "../../library/allauth";
import { getCSRFToken } from "../../library/django";
// Helper to get fetch options
const getFetchOptions = () => {
  let baseUrl = "";
  try {
    baseUrl = settings.baseUrl;
  } catch {}

  const options = { headers: {} };

  if (settings.client === Client.BROWSER) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      options.headers["X-CSRFToken"] = csrfToken;
    }
  }
  if (settings.withCredentials) {
    options.credentials = "include";
  }

  return { baseUrl, options };
};

export const Video = ({
  selectedVideo,
  getFileUrl,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const videoRef = useRef(null);

  // Load video when selectedVideo changes
  useEffect(() => {
    const loadVideo = async () => {
      if (!selectedVideo || !selectedVideo.videoToken) {
        setVideoUrl(null);
        return;
      }

      setIsLoadingVideo(true);
      setVideoError(null);
      setIsPlaying(false);

      try {
        
        // Get video URL using token
        const url = await getFileUrl(selectedVideo.videoToken);
        
        if (!url) {
          throw new Error("Failed to get video URL");
        }

        setVideoUrl(url);

        // Reset current time when new video loads
        setCurrentTime(0);
      } catch (error) {
        console.error("Error loading video:", error);
        setVideoError("Failed to load video");
      } finally {
        setIsLoadingVideo(false);
      }
    };

    loadVideo();

    // Cleanup: Revoke object URL when component unmounts or video changes
    return () => {
      if (videoUrl && videoUrl.startsWith("blob:")) {
        window.URL.revokeObjectURL(videoUrl);
      }
    };
  }, [selectedVideo, getFileUrl]);

  // Sync video element with videoUrl
  useEffect(() => {
    if (videoRef.current && videoUrl) {
      videoRef.current.load();
    }
  }, [videoUrl]);

  const handleTimeUpdate = (e) => {
    setCurrentTime(e.target.currentTime);
  };

  const handleLoadedMetadata = (e) => {
    const videoDuration = e.target.duration;
    if (videoDuration && !isNaN(videoDuration)) {
      setDuration(videoDuration);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const skipBackward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(
        0,
        videoRef.current.currentTime - 10
      );
    }
  };

  const skipForward = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(
        videoRef.current.duration,
        videoRef.current.currentTime + 10
      );
    }
  };

  const handleSeek = (e) => {
    if (!videoRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const newTime = pos * duration;
    if (!isNaN(newTime)) {
      videoRef.current.currentTime = newTime;
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.mozRequestFullScreen) {
        videoRef.current.mozRequestFullScreen();
      }
    }
  };

  const handleDownload = async () => {
    if (!selectedVideo || !selectedVideo.videoToken) return;

    try {
      const { baseUrl, options } = getFetchOptions();

      const response = await fetch(`${baseUrl}/file/get_file/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...options.headers,
        },
        credentials: options.credentials,
        body: JSON.stringify({ token: selectedVideo.videoToken }),
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = selectedVideo.filename || "video.mp4";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading video:", error);
      alert("Failed to download video");
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === null) return "00:00";
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div className="rounded-lg shadow-sm overflow-hidden h-full flex flex-col">
      <div className="relative flex-1 min-h-0 bg-black">
        {/* Video Element */}
        {videoUrl ? (
          <video
            ref={videoRef}
            className="w-full h-full object-contain"
            src={videoUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            playsInline
          />
        ) : null}

        {/* Loading Overlay */}
        {isLoadingVideo && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-3" />
              <p className="text-white text-sm">Loading video...</p>
            </div>
          </div>
        )}

        {/* Error Overlay */}
        {videoError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90">
            <div className="text-center text-white">
              <p className="text-lg mb-2">⚠️ Error</p>
              <p className="text-sm">{videoError}</p>
            </div>
          </div>
        )}

        {/* No Video Selected Overlay */}
        {!selectedVideo && !isLoadingVideo && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
            <div className="text-center text-white">
              <Play size={64} className="mx-auto mb-4 opacity-50" />
              <p className="text-xl mb-2">No Video Selected</p>
              <p className="text-sm text-gray-400">
                Apply filters and select a video from the playlist
              </p>
            </div>
          </div>
        )}

        {/* Video Title Overlay */}
        {selectedVideo && videoUrl && (
          <h1 className="absolute top-4 left-4 text-2xl font-semibold bg-white/90 px-3 py-1.5 rounded">
            {selectedVideo.title || "Video"}
          </h1>
        )}

        {/* Play/Pause Overlay */}
        {videoUrl && !isLoadingVideo && (
          <div
            onClick={togglePlay}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white/70 cursor-pointer hover:text-white/90 transition-colors"
          >
            {/* {isPlaying ? <Pause size={100} /> : <Play size={100} />} */}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="bg-gray-900 px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <button
              onClick={skipBackward}
              className="text-white p-1 hover:text-gray-300 disabled:opacity-50"
              disabled={!videoUrl}
            >
              <UndoDot size={20} />
            </button>
            <button
              onClick={togglePlay}
              className="text-white p-1 hover:text-gray-300 disabled:opacity-50"
              disabled={!videoUrl}
            >
              {isPlaying ? <Pause size={24} /> : <Play size={24} />}
            </button>
            <button
              onClick={skipForward}
              className="text-white p-1 hover:text-gray-300 disabled:opacity-50"
              disabled={!videoUrl}
            >
              <RedoDot size={20} />
            </button>
            <span className="text-sm ml-3">
              <span className="text-red-400">{formatTime(currentTime)}</span>
              <span className="text-gray-400"> / {formatTime(duration)}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Volume Control */}
            <div className="flex items-center gap-2 group">
              <button
                onClick={toggleMute}
                className="text-white p-1 hover:text-gray-300"
              >
                {isMuted || volume === 0 ? (
                  <VolumeX size={20} />
                ) : (
                  <Volume2 size={20} />
                )}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-20 opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="text-white p-1 hover:text-gray-300 disabled:opacity-50"
              disabled={!selectedVideo}
              title="Download video"
            >
              <Download size={20} />
            </button>

            {/* Fullscreen */}
            <button
              onClick={handleFullscreen}
              className="text-white p-1 hover:text-gray-300 disabled:opacity-50"
              disabled={!videoUrl}
            >
              <Maximize2 size={20} />
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div
          onClick={handleSeek}
          className="w-full bg-gray-700 h-1 rounded-full overflow-hidden cursor-pointer"
        >
          <div
            className="bg-red-500 h-full rounded-full transition-all duration-200"
            style={{
              width: `${duration ? (currentTime / duration) * 100 : 0}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
};